"use client"

import { ROUTES, SECURED_PLAN_PARAM, segmentsAfterRegion } from "@/lib/routes"
import { env } from "@/lib/runtime/env"
import { retryOnce } from "@/lib/runtime/retry-once"
import { readSessionScoped, writeSessionScoped } from "@/platform/browser/session-store"
import { recordTelemetry } from "@/platform/observability/telemetry"

import { campaignContext, fullySampledCampaign } from "./campaign-sampling"
import { browserLogs } from "./logs"

import type { FullySampledCampaign } from "./campaign-sampling"
import type { DatadogSdk, SdkConfig } from "./sdk"

type Operation = (sdk: DatadogSdk) => void
type Tier = "browse" | "checkout" | "full"

const CHECKOUT_SAMPLE_RATE = 20
const CHECKOUT_SECTIONS: readonly string[] = [ROUTES.CHECKOUT, ROUTES.VERIFY, "enlace"]
const CHECKOUT_ACTIONS: readonly string[] = ["add_to_cart", "begin_checkout"]
const DRAW_KEY = "nelo_rum_draw"
const TRACKED_KEY = "nelo_rum_tracked"
const IDLE_TIMEOUT_MS = 2000
const MAX_EARLY_ERRORS = 20
const MAX_QUEUED_OPERATIONS = 100

let sdk: DatadogSdk | null = null
let started = false
let disabled = false
let tracked = false
let draw: number | null = null
let campaign: FullySampledCampaign | null = null
let loadNow = (): void => {}
let queue: Operation[] = []
let earlyErrors: unknown[] = []
const globalProperties = new Set<string>()
let userContext: Record<string, unknown> = {}
const globalContext: Record<string, unknown> = {}

const call = (operation: Operation, required = false): void => {
  if (typeof window === "undefined" || disabled) return
  if (sdk) {
    const ready = sdk
    recordTelemetry(() => operation(ready))
  } else if (required || queue.length < MAX_QUEUED_OPERATIONS) queue.push(operation)
}

const browseSampleRate = (): number => (env.datadogSessionSampleRate ? Number(env.datadogSessionSampleRate) : 10)

const sampleRate = (tier: Tier): number => {
  if (tier === "full") return 100
  return tier === "checkout" ? Math.max(CHECKOUT_SAMPLE_RATE, browseSampleRate()) : browseSampleRate()
}

const locationTier = (): Tier => {
  if (campaign) return "full"
  const [section, next] = segmentsAfterRegion(window.location.pathname)
  const secured = new URLSearchParams(window.location.search).get(SECURED_PLAN_PARAM) === "1"
  if (section === ROUTES.MERCHANT_CHECKOUT || (section === ROUTES.CHECKOUT && (next === "comercio" || secured))) return "full"
  return section !== undefined && CHECKOUT_SECTIONS.includes(section) ? "checkout" : "browse"
}

const sessionDraw = (): number => {
  const stored = Number(readSessionScoped(DRAW_KEY) ?? Number.NaN)
  if (Number.isFinite(stored)) return stored
  const fresh = Math.random() * 100
  writeSessionScoped(DRAW_KEY, String(fresh))
  return fresh
}

const sample = (tier: Tier): void => {
  if (tracked || disabled || typeof window === "undefined") return
  draw ??= sessionDraw()
  if (draw >= sampleRate(tier)) return
  tracked = true
  writeSessionScoped(TRACKED_KEY, "1")
  const ready = sdk
  if (ready) recordTelemetry(() => ready.startTracking())
  else loadNow()
}

export const sampleLocation = (): void => sample(locationTier())

const recordEarlyError = (reason: unknown): void => {
  if (earlyErrors.length < MAX_EARLY_ERRORS) earlyErrors.push(reason)
}

const watchEarlyErrors = (): (() => void) => {
  const onError = (event: ErrorEvent) => recordEarlyError(event.error ?? new Error(event.message))
  const onRejection = (event: PromiseRejectionEvent) => recordEarlyError(event.reason)
  window.addEventListener("error", onError)
  window.addEventListener("unhandledrejection", onRejection)
  return () => {
    window.removeEventListener("error", onError)
    window.removeEventListener("unhandledrejection", onRejection)
  }
}

const disable = (): void => {
  disabled = true
  queue = []
  earlyErrors = []
}

const startSdkSafely = async (config: SdkConfig): Promise<DatadogSdk | null> => {
  try {
    const { startSdk } = await retryOnce(() => import("./sdk"))
    return startSdk(config)
  } catch {
    return null
  }
}

const load = async (config: SdkConfig, stopWatching: () => void): Promise<void> => {
  const ready = await startSdkSafely(config)
  stopWatching()
  if (!ready) return disable()
  sdk = ready
  const pending = queue
  queue = []
  for (const operation of pending) recordTelemetry(() => operation(ready))
  for (const error of earlyErrors) recordTelemetry(() => ready.addError(error, { type: "error_before_datadog_init" }))
  earlyErrors = []
  if (tracked) recordTelemetry(() => ready.startTracking())
}

const startWhenLeaving = (run: () => void): (() => void) => {
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") run()
  }
  window.addEventListener("pagehide", run)
  document.addEventListener("visibilitychange", onVisibilityChange)
  return () => {
    window.removeEventListener("pagehide", run)
    document.removeEventListener("visibilitychange", onVisibilityChange)
  }
}

const whenIdle = (run: () => void): void => {
  const schedule = () => {
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS })
    else window.setTimeout(run, IDLE_TIMEOUT_MS)
  }
  if (document.readyState === "complete") schedule()
  else window.addEventListener("load", schedule, { once: true })
}

export const startDatadog = ({ immediately = false }: { immediately?: boolean } = {}): void => {
  if (typeof window === "undefined" || started || disabled) return
  const applicationId = env.datadogApplicationId
  const clientToken = env.datadogClientToken
  if (!applicationId || !clientToken) return disable()
  started = true
  campaign = fullySampledCampaign()
  if (campaign) for (const [key, value] of Object.entries(campaignContext(campaign))) datadog.setGlobalContextProperty(key, value)
  tracked = readSessionScoped(TRACKED_KEY) === "1"
  const tier = locationTier()
  sample(tier)
  const stopWatching = watchEarlyErrors()
  let begun = false
  let stopWatchingLeave = () => {}
  const run = () => {
    if (begun) return
    begun = true
    stopWatchingLeave()
    void load({ applicationId, clientToken, campaign }, stopWatching)
  }
  loadNow = run
  if (immediately || tier !== "browse") return run()
  stopWatchingLeave = startWhenLeaving(run)
  whenIdle(run)
}

export const datadog = {
  addAction: (name: string, context?: Record<string, unknown>): void => {
    if (CHECKOUT_ACTIONS.includes(name)) sample("checkout")
    call((target) => target.addAction(name, context))
  },
  addError: (error: unknown, context?: Record<string, unknown>): void => call((target) => target.addError(error, context)),
  addFeatureFlagEvaluation: (key: string, value: unknown): void => call((target) => target.addFeatureFlagEvaluation(key, value), true),
  setUser: (user: { id: string } & Record<string, unknown>): void => {
    const retained = Object.fromEntries(
      Object.entries(userContext).filter(([key]) => key === "nelo_pseudo_id" || key === "is_react_native_webview" || key.startsWith("experiment."))
    )
    userContext = { ...retained, ...(userContext.id === user.id ? userContext : {}), ...user }
    const captured = { ...userContext } as { id: string } & Record<string, unknown>
    browserLogs.setUser(captured)
    call((target) => target.setUser(captured), true)
  },
  setUserProperty: (key: string, value: unknown): void => {
    userContext[key] = value
    browserLogs.setUserProperty(key, value)
    call((target) => target.setUserProperty(key, value), true)
  },
  clearUser: (): void => {
    userContext = {}
    browserLogs.clearUser()
    call((target) => target.clearUser(), true)
  },
  getUser: (): Record<string, unknown> => ({ ...userContext }),
  getGlobalContext: (): Record<string, unknown> => ({ ...globalContext }),
  setGlobalContextProperty: (key: string, value: unknown): void => {
    globalProperties.add(key)
    globalContext[key] = value
    browserLogs.setGlobalContextProperty(key, value)
    call((target) => target.setGlobalContextProperty(key, value), true)
  },
  removeGlobalContextProperty: (key: string): void => {
    globalProperties.delete(key)
    delete globalContext[key]
    browserLogs.removeGlobalContextProperty(key)
    call((target) => target.removeGlobalContextProperty(key), true)
  },
  hasGlobalContextProperty: (key: string): boolean => globalProperties.has(key),
  logInfo: (message: string, context?: Record<string, unknown>): void => browserLogs.info(message, context),
  logWarn: (message: string, context?: Record<string, unknown>, error?: Error): void => browserLogs.warn(message, context, error),
  logError: (message: string, context?: Record<string, unknown>, error?: Error): void => browserLogs.error(message, context, error),
}
